import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

const OperationsReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeRange, setTimeRange] = useState('last30Days');
  
  const reportData = {
    processData: [
      { name: 'Manufacturing', value: 45, color: '#5AB9EA' },
      { name: 'Quality Control', value: 30, color: '#84CEEB' },
      { name: 'Logistics', value: 25, color: '#5680E9' },
      { name: 'Inventory', value: 20, color: '#F8A055' },
      { name: 'Maintenance', value: 15, color: '#C1C8E4' },
      { name: 'Supply Chain', value: 35, color: '#8860D0' }
    ],
    efficiencyData: [
      { month: 'Jan', efficiency: 85, downtime: 12, cost: 45000 },
      { month: 'Feb', efficiency: 88, downtime: 10, cost: 42000 },
      { month: 'Mar', efficiency: 90, downtime: 8, cost: 40000 },
      { month: 'Apr', efficiency: 92, downtime: 7, cost: 38000 },
      { month: 'May', efficiency: 94, downtime: 5, cost: 35000 },
      { month: 'Jun', efficiency: 95, downtime: 4, cost: 32000 }
    ],
    resourceData: [
      { resource: 'Equipment', utilization: 92 },
      { resource: 'Workforce', utilization: 88 },
      { resource: 'Facilities', utilization: 85 },
      { resource: 'Technology', utilization: 90 },
      { resource: 'Materials', utilization: 87 },
      { resource: 'Energy', utilization: 82 }
    ],
    kpiData: [
      { metric: 'Production Target', target: 1000, actual: 980, variance: -2 },
      { metric: 'Quality Score', target: 98, actual: 97.5, variance: -0.5 },
      { metric: 'On-Time Delivery', target: 95, actual: 96, variance: 1 },
    ],
    metrics: {
      overallEfficiency: 92.5,
      totalDowntime: 46,
      avgResponseTime: '2h 15m',
      costSavings: 123000,
      qualityScore: 97.5,
      inventoryTurnover: 8.2,
      avgLeadTime: '3.5 days',
      resourceUtilization: 88.5
    },
    topInitiatives: [
      { title: 'Process Automation Implementation', impact: '15% efficiency gain' },
      { title: 'Lean Manufacturing Training', impact: '10% waste reduction' },
      { title: 'Supply Chain Optimization', impact: '20% cost reduction' }
    ],
    clientSegments: [
      { name: '$1M - $2M', value: 15, color: '#4F46E5' },
      { name: '$2M - $4M', value: 42, color: '#7C3AED' },
      { name: '$4M - $6M', value: 85, color: '#EC4899' },
      { name: '$6M - $8M', value: 128, color: '#F59E0B' },
      { name: '$8M - $10M', value: 8, color: '#10B981' },
      { name: '$10M+', value: 5, color: '#3B82F6' }
    ],
    clientStages: [
      { stage: 'Onboarding', count: 28, value: 420000 },
      { stage: 'Active', count: 156, value: 2340000 },
      { stage: 'Recurring', count: 98, value: 1470000 },
      { stage: 'Paused', count: 15, value: 225000 }
    ],
    overdueTasks: [
      { name: 'Sales', value: 12, color: '#4F46E5' },
      { name: 'Operations', value: 8, color: '#7C3AED' },
      { name: 'Technology', value: 15, color: '#EC4899' },
      { name: 'Finance', value: 5, color: '#F59E0B' },
      { name: 'Marketing', value: 7, color: '#10B981' },
      { name: 'HR', value: 3, color: '#3B82F6' }
    ],
    firesMetrics: {
      internalFires: 12,
      clientFires: 8,
      avgNeedsAttention: 3.5,
      avgPending: 2.2,
      avgResolved: 4.8
    },
    touchpointMetrics: {
      avgMeetingsPerClient: 3.2,
      avgCallsPerClient: 4.5,
      avgTimePerClient: 6.8,
      overServedClients: 12,
      underServedClients: 8,
      avgDaysBetweenTouchpoints: 4.5,
      avgDaysBetweenActivity: 3.2
    },
    taskMetrics: [
      { pillar: 'Sales', total: 28, overdue: 12, completed: 16 },
      { pillar: 'Operations', value: 42, overdue: 8, completed: 34 },
      { pillar: 'Technology', value: 35, overdue: 15, completed: 20 },
      { pillar: 'Finance', value: 19, overdue: 5, completed: 14 },
      { pillar: 'Marketing', value: 24, overdue: 7, completed: 17 },
      { pillar: 'HR', value: 15, overdue: 3, completed: 12 }
    ],
  };

  // Calculate metrics
  const totalCost = reportData.efficiencyData.reduce((sum, item) => sum + item.cost, 0);
  const avgEfficiency = reportData.efficiencyData.reduce((sum, item) => sum + item.efficiency, 0) / reportData.efficiencyData.length;
  const totalDowntime = reportData.efficiencyData.reduce((sum, item) => sum + item.downtime, 0);
  const costPerUnit = totalCost / reportData.kpiData[0].actual;
  const efficiencyGain = ((reportData.efficiencyData[5].efficiency - reportData.efficiencyData[0].efficiency) / reportData.efficiencyData[0].efficiency) * 100;

  const handleExport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.text('Operations Analytics Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Overview Section
    doc.setFontSize(16);
    doc.text('Overview', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    doc.text(`Overall Efficiency: ${reportData.metrics.overallEfficiency}%`, 20, yPos);
    yPos += 7;
    doc.text(`Total Cost Savings: $${reportData.metrics.costSavings.toLocaleString()}`, 20, yPos);
    yPos += 7;
    doc.text(`Resource Utilization: ${reportData.metrics.resourceUtilization}%`, 20, yPos);
    yPos += 15;

    // Process Performance
    doc.setFontSize(16);
    doc.text('Process Performance', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    doc.text(`Quality Score: ${reportData.metrics.qualityScore}%`, 20, yPos);
    yPos += 7;
    doc.text(`Inventory Turnover: ${reportData.metrics.inventoryTurnover}x`, 20, yPos);
    yPos += 15;

    // Resource Utilization
    doc.setFontSize(16);
    doc.text('Resource Utilization', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    reportData.resourceData.forEach(resource => {
      doc.text(`${resource.resource}: ${resource.utilization}% utilization`, 20, yPos);
      yPos += 7;
    });
    yPos += 8;

    // KPI Performance
    doc.setFontSize(16);
    doc.text('KPI Performance', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    reportData.kpiData.forEach(kpi => {
      doc.text(`${kpi.metric}: ${kpi.actual} (Target: ${kpi.target}, Variance: ${kpi.variance}%)`, 20, yPos);
      yPos += 7;
    });

    // Save the PDF
    doc.save('operations-analytics-report.pdf');
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/reports')} />
          <h1 className={`text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white`}>Operations Analytics</h1>
          <div className="w-[100px]"></div> {/* Spacer to balance the back button */}
        </div>

        <div className="flex justify-end items-center mb-6">
          <div className="flex items-center space-x-4">
            <select 
              className={`px-4 py-2 border rounded-md shadow-sm ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300'
              }`}
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="last7Days">Last 7 Days</option>
              <option value="last30Days">Last 30 Days</option>
              <option value="last90Days">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
            </select>
            <button 
              onClick={handleExport}
              className="px-4 py-2 bg-red-600 hover:bg-red-800 text-white rounded-md transition-colors duration-200"
            >
              Export
            </button>
          </div>
        </div>

        {/* Overview Section */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`flex flex-col items-center p-4 rounded-lg ${
              darkMode ? 'bg-blue-900' : 'bg-blue-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Active Clients</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>245</span>
            </div>
            <div className={`flex flex-col items-center p-4 rounded-lg ${
              darkMode ? 'bg-green-900' : 'bg-green-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>Client Retention Rate</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>92%</span>
            </div>
            <div className={`flex flex-col items-center p-4 rounded-lg ${
              darkMode ? 'bg-purple-900' : 'bg-purple-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-purple-300' : 'text-gray-500'}`}>Overdue Tasks</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-purple-100' : 'text-gray-900'}`}>12</span>
            </div>
          </div>
        </div>

        {/* Process Performance & Efficiency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Client Segments</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reportData.clientSegments}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={150}
                    label
                  >
                    {reportData.clientSegments.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Clients by Stage</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.clientStages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [`${value} clients`, name]}
                    labelFormatter={(label) => `${label} Stage`}
                  />
                  <Legend />
                  <Bar dataKey="count" name="Number of Clients" fill="#4F46E5" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Resource Utilization & KPI Performance */}
        <div className="mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tasks by Pillar</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.taskMetrics} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="pillar" type="category" width={120} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" name="Completed On-time" stackId="a" fill="#10B981" />
                  <Bar dataKey="overdue" name="Overdue" stackId="a" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Cost Analysis & Initiatives */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Internal Fires</h2>
            <div className="grid grid-cols-1 gap-4">
              <div className={`p-4 rounded-xl text-center bg-red-50 dark:bg-red-900/40 border border-red-100 dark:border-red-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-red-300' : 'text-gray-500'}`}>Internal Fires</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-red-200' : 'text-red-600'}`}>{reportData.firesMetrics.internalFires}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-100 dark:border-yellow-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-yellow-300' : 'text-gray-500'}`}>Avg Days in Needs Attention</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-200' : 'text-yellow-600'}`}>{reportData.firesMetrics.avgNeedsAttention}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Avg Days in Pending</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>{reportData.firesMetrics.avgPending}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-green-50 dark:bg-green-900/40 border border-green-100 dark:border-green-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>Avg Days until Resolved</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-green-200' : 'text-green-600'}`}>{reportData.firesMetrics.avgResolved}</div>
              </div>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Client Fires</h2>
            <div className="grid grid-cols-1 gap-4">
              <div className={`p-4 rounded-xl text-center bg-orange-50 dark:bg-orange-900/40 border border-orange-100 dark:border-orange-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-orange-300' : 'text-gray-500'}`}>Client Fires</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-orange-200' : 'text-orange-600'}`}>{reportData.firesMetrics.clientFires}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-100 dark:border-yellow-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-yellow-300' : 'text-gray-500'}`}>Avg Days in Needs Attention</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-200' : 'text-yellow-600'}`}>{reportData.firesMetrics.avgNeedsAttention}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Avg Days in Pending</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>{reportData.firesMetrics.avgPending}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-green-50 dark:bg-green-900/40 border border-green-100 dark:border-green-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>Avg Days until Resolved</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-green-200' : 'text-green-600'}`}>{reportData.firesMetrics.avgResolved}</div>
              </div>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Client Touchpoints</h2>
            <div className="grid grid-cols-1 gap-4">
              <div className={`p-4 rounded-xl text-center bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Avg. Meetings per Client</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>{reportData.touchpointMetrics.avgMeetingsPerClient}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-indigo-300' : 'text-gray-500'}`}>Avg. Hours per Client</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-indigo-200' : 'text-indigo-600'}`}>{reportData.touchpointMetrics.avgTimePerClient}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-purple-50 dark:bg-purple-900/40 border border-purple-100 dark:border-purple-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-purple-300' : 'text-gray-500'}`}>Avg Days Between Touchpoints</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-purple-200' : 'text-purple-600'}`}>{reportData.touchpointMetrics.avgDaysBetweenTouchpoints}</div>
              </div>
              <div className={`p-4 rounded-xl text-center bg-pink-50 dark:bg-pink-900/40 border border-pink-100 dark:border-pink-800/50`}>
                <div className={`text-sm mb-1 ${darkMode ? 'text-pink-300' : 'text-gray-500'}`}>Avg Days Between Activity</div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-pink-200' : 'text-pink-600'}`}>{reportData.touchpointMetrics.avgDaysBetweenActivity}</div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Insights */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI-Powered Operations Insights</h2>
          <div className={`p-4 rounded-xl mb-4 bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Optimization Recommendations</h3>
            <ul className="list-disc pl-5">
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Implement predictive maintenance to reduce downtime by 25%</li>
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Optimize inventory levels based on demand forecasting</li>
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Automate quality control processes to improve efficiency</li>
            </ul>
          </div>
          <div className={`p-4 rounded-xl bg-purple-50 dark:bg-purple-900/40 border border-purple-100 dark:border-purple-800/50`}>
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-purple-200' : 'text-gray-900'}`}>Performance Predictions</h3>
            <p className={darkMode ? 'text-purple-200' : 'text-gray-900'}>Based on current trends, we predict a 15% improvement in overall efficiency next quarter if recommended optimizations are implemented.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsReports; 